import { create } from 'zustand'
import { supabase } from '@/integrations/supabase/client'
import { logger } from '@/lib/services/logger-service'
import { SortOption } from '@/lib/services/user-preferences-service'

interface ActiveChatInfo {
  caregiverId: string
  caregiverName: string
  caregiverEmail?: string
  specialistId: string
  roomId?: string
}

interface VideoCallInfo {
  callId: string
  roomName: string
  token: string
  wsUrl: string
  caregiverId: string
  caregiverName: string
  specialistId: string
}

interface DashboardState {
  // State
  searchQuery: string
  activeChatInfo: ActiveChatInfo | null
  selectedCaregiverId: string | null
  createCaregiverDialogOpen: boolean
  agreementDialogOpen: boolean
  currentPage: number
  pageSize: number
  sortBy: SortOption
  sortOrder: 'asc' | 'desc'
  
  // Video call state
  activeVideoCall: VideoCallInfo | null
  videoCallDialogOpen: boolean
  videoCallTarget: { caregiverId: string; caregiverName: string } | null
  
  // Actions
  setSearchQuery: (query: string) => void
  setActiveChatInfo: (info: ActiveChatInfo | null) => void
  setSelectedCaregiverId: (id: string | null) => void
  setCreateCaregiverDialogOpen: (open: boolean) => void
  setAgreementDialogOpen: (open: boolean) => void
  setCurrentPage: (page: number) => void
  setPageSize: (size: number) => void
  setSortBy: (sortBy: SortOption) => void
  setSortOrder: (order: 'asc' | 'desc') => void
  
  // Video call actions
  setActiveVideoCall: (info: VideoCallInfo | null) => void
  openVideoCallDialog: (caregiverId: string, caregiverName: string) => void
  closeVideoCallDialog: () => void
  
  // Complex actions
  openCreateCaregiverDialog: () => void
  closeCreateCaregiverDialog: () => void
  openAgreementDialog: (caregiverId: string) => void
  closeAgreementDialog: () => void
  handleChatClick: (caregiverId: string, caregiverName: string, specialistId: string, caregiverEmail?: string) => Promise<void>
  handleCloseChat: () => void
  
  // Preferences
  loadPreferences: (specialistId: string) => Promise<void>
  savePreferences: (specialistId: string) => Promise<void>
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  // Initial state
  searchQuery: '',
  activeChatInfo: null,
  selectedCaregiverId: null,
  createCaregiverDialogOpen: false,
  agreementDialogOpen: false,
  currentPage: 1,
  pageSize: 10,
  sortBy: 'active-first',
  sortOrder: 'asc',
  
  // Video call initial state
  activeVideoCall: null,
  videoCallDialogOpen: false,
  videoCallTarget: null,
  
  // Simple setters
  setSearchQuery: (query) => {
    set({ searchQuery: query })
    const specialistId = get().activeChatInfo?.specialistId
    if (specialistId) {
      get().savePreferences(specialistId)
    }
  },
  
  setActiveChatInfo: (info) => set({ activeChatInfo: info }),
  setSelectedCaregiverId: (id) => set({ selectedCaregiverId: id }),
  setCreateCaregiverDialogOpen: (open) => set({ createCaregiverDialogOpen: open }),
  setAgreementDialogOpen: (open) => set({ agreementDialogOpen: open }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setPageSize: (size) => set({ pageSize: size, currentPage: 1 }),
  
  setSortBy: (sortBy) => {
    set({ sortBy })
    const specialistId = get().activeChatInfo?.specialistId
    if (specialistId) {
      get().savePreferences(specialistId)
    }
  },
  
  setSortOrder: (order) => {
    set({ sortOrder: order })
    const specialistId = get().activeChatInfo?.specialistId
    if (specialistId) {
      get().savePreferences(specialistId)
    }
  },
  
  // Video call actions
  setActiveVideoCall: (info) => set({ activeVideoCall: info }),
  
  openVideoCallDialog: (caregiverId, caregiverName) => {
    logger.debug('Opening video call dialog', { caregiverId, caregiverName })
    set({ 
      videoCallDialogOpen: true,
      videoCallTarget: { caregiverId, caregiverName }
    })
  },
  
  closeVideoCallDialog: () => {
    logger.debug('Closing video call dialog')
    set({ 
      videoCallDialogOpen: false,
      videoCallTarget: null
    })
  },
  
  // Complex actions
  openCreateCaregiverDialog: () => {
    logger.debug('Opening create caregiver dialog')
    set({ createCaregiverDialogOpen: true })
  },
  
  closeCreateCaregiverDialog: () => {
    logger.debug('Closing create caregiver dialog')
    set({ createCaregiverDialogOpen: false })
  },
  
  openAgreementDialog: (caregiverId) => {
    logger.debug('Opening agreement dialog', { caregiverId })
    set({ 
      selectedCaregiverId: caregiverId,
      agreementDialogOpen: true 
    })
  },
  
  closeAgreementDialog: () => {
    logger.debug('Closing agreement dialog')
    set({ 
      agreementDialogOpen: false,
      selectedCaregiverId: null 
    })
  },
  
  handleChatClick: async (caregiverId, caregiverName, specialistId, caregiverEmail) => {
    logger.debug('Opening chat', { caregiverId, caregiverName, specialistId })
    
    try {
      // Check if room exists
      const { data: existingRoom, error: roomError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('specialist_id', specialistId)
        .eq('caregiver_id', caregiverId)
        .single()

      if (roomError && roomError.code !== 'PGRST116') {
        logger.error('Error checking for existing room', { error: roomError })
      }

      let roomId = existingRoom?.id

      // Create room if it doesn't exist
      if (!roomId) {
        const { data: newRoom, error: createError } = await supabase
          .from('chat_rooms')
          .insert({
            specialist_id: specialistId,
            caregiver_id: caregiverId,
          })
          .select('id')
          .single()

        if (createError) {
          logger.error('Error creating chat room', { error: createError })
          throw createError
        }

        roomId = newRoom.id
        logger.debug('Created new chat room', { roomId })
      }

      set({
        activeChatInfo: {
          caregiverId,
          caregiverName,
          caregiverEmail,
          specialistId,
          roomId
        }
      })
    } catch (error) {
      logger.error('Failed to initialize chat', { error })
      throw error
    }
  },
  
  handleCloseChat: () => {
    logger.debug('Closing chat')
    set({ activeChatInfo: null })
  },
  
  // Preferences management
  loadPreferences: async (specialistId) => {
    try {
      const { data, error } = await supabase
        .from('specialists')
        .select('ai_preferences')
        .eq('id', specialistId)
        .single()

      if (error) throw error

      const prefs = data?.ai_preferences as any
      if (prefs?.dashboard) {
        set({
          searchQuery: prefs.dashboard.searchQuery || '',
          sortBy: prefs.dashboard.sortBy || 'name',
          sortOrder: prefs.dashboard.sortOrder || 'asc',
        })
        logger.debug('Loaded dashboard preferences', { preferences: prefs.dashboard })
      }
    } catch (error) {
      logger.error('Failed to load dashboard preferences', { error })
    }
  },
  
  savePreferences: async (specialistId) => {
    try {
      const state = get()
      const preferences = {
        searchQuery: state.searchQuery,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      }

      const { data: current } = await supabase
        .from('specialists')
        .select('ai_preferences')
        .eq('id', specialistId)
        .single()

      const updatedPrefs = {
        ...(current?.ai_preferences as any || {}),
        dashboard: preferences
      }

      const { error } = await supabase
        .from('specialists')
        .update({ ai_preferences: updatedPrefs })
        .eq('id', specialistId)

      if (error) throw error

      logger.debug('Saved dashboard preferences', { preferences })
    } catch (error) {
      logger.error('Failed to save dashboard preferences', { error })
    }
  },
}))
